import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../../pixiSettings";
import anime from "animejs/lib/anime.es.js";
import FreeSpinTotalWinController, {
  HOLD_AND_WIN_TOTAL_WIN_SPINE,
} from "./FreeSpinTotalWinController";
import { safeInit } from "../../../../utilities";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import { selectShowFreeSpinTotalWin } from "../../../../State/Slices/animationsSlice";
import {
  selectShowHoldAndWinTotalWin,
  selectHoldAndWinWin,
  setShowHoldAndWinTotalWin,
} from "../../../../State/Slices/holdAndWinSlice";
import { freeSpinOutroBet } from "../../../../Games/Shared/freeSpinOutroPopup";
import { fsDiag } from "../../../../utils/fsDiag";

/**
 * FreeSpinTotalWinDialog — everything won across the free-spin feature.
 *
 * The `total_win` skin of the shared win_celebration spine, shown when the
 * feature ends. Separate from PotCollectionOutroDialog so it can diverge.
 *
 *   - The component NEVER unmounts. Visibility is alpha, which is what gives
 *     the spine's `out` a chance to play before the flag clears.
 *   - The overlay fades through `overlayRef`, driven by the controller.
 *   - Closing is the controller's job: `out`, then the flag, then the fade.
 */

const OVERLAY_BACKGROUND_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const OVERLAY_BACKGROUND_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;
const OVERLAY_FADE_DURATION = POPUP_OVERLAY_CONFIG.FADE_DURATION;

// variant "hold_and_win" — the same popup and animation for the end of Hold &
// Win (Vladimir, 2026-09-29: "we play mad labs total win but hold and win
// style"). Its own flag, the round's paid amount, and its own close flag;
// the close hands over to the same wipe (TransitionDialog, out of H&W).
const FreeSpinTotalWinDialog = ({ variant = "free_spins" }) => {
  const isHoldAndWin = variant === "hold_and_win";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showFreeSpins = useSelector(selectShowFreeSpinTotalWin);
  const showHoldAndWin = useSelector(selectShowHoldAndWinTotalWin);
  const show = isHoldAndWin ? showHoldAndWin : showFreeSpins;
  // The ROUND's total, accumulated across every free spin — not
  // game.currentRound.win, which is the base game's last spin and, on a
  // trigger, is 0. That is what this popup was showing.
  const freeSpinsTotal = useSelector((state) => state.freeSpins?.totalWin ?? 0);
  // Hold & Win: what the settling respin PAID (outcome.win, capped).
  const holdAndWinTotal = useSelector(selectHoldAndWinWin);
  // const totalWin = isHoldAndWin ? holdAndWinTotal : freeSpinsTotal;
  // (totalWin is set below, after effectiveBet — the dev preview needs it.)
  const holdAndWinActive = useSelector((state) => state.holdAndWin?.active);

  // ── ONE denominator for every outro popup ──
  //
  // The stake the round was PLAYED at, not the price and not the live bet.
  // This was three inline copies of the same rule, one per popup, while the
  // threshold that CHOOSES between them used a fourth. freeSpinOutroPopup owns
  // it now, and carries the full reasoning plus the two wrong sources that
  // were tried first.
  const effectiveBet = useSelector(freeSpinOutroBet);
  // The dev panel's H&W preview has no round (step.win 0) — it counts to
  // 125x bet instead, so the number can be sized (dev only).
  const previewTotal =
    FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL &&
    isHoldAndWin &&
    !holdAndWinActive &&
    !holdAndWinTotal
      ? (effectiveBet || 1) * 125
      : 0;
  const totalWin = isHoldAndWin
    ? holdAndWinTotal || previewTotal
    : freeSpinsTotal;

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolHeight: uiSymbolSize, device } = dimensionsContext;

  const [controllerReady, setControllerReady] = useState(false);
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          if (typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          setOverlayAlpha(0);
          if (typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once — it lives for the session.
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new FreeSpinTotalWinController(
          containerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
          // ...(isHoldAndWin ? [setShowHoldAndWinTotalWin] : []),
          // Hold & Win: its own close flag AND its own spine (2026-09-30).
          // ...(isHoldAndWin
          //   ? [setShowHoldAndWinTotalWin, HOLD_AND_WIN_TOTAL_WIN_SPINE]
          //   : []),
          // Free spins draws the Hold & Win outro too — it has no total win
          // art of its own yet (win_celebration dropped `total_win`,
          // 2026-09-30). `undefined` keeps the free-spins close flag.
          isHoldAndWin ? setShowHoldAndWinTotalWin : undefined,
          HOLD_AND_WIN_TOTAL_WIN_SPINE,
        );
        // ── Rendered, so the effect below can depend on it ──
        //
        // See TransitionDialog and BonusGameView, which carry the same flag
        // for the same reason. A ref is invisible to React: the start effect
        // was keyed on [show] alone, so if `show` turned true BEFORE this
        // controller existed the effect fired once against a null ref and
        // never ran again — `show` does not change a second time.
        //
        // That is exactly what a completion reload does: the round is already
        // over in the store before this component mounts, so the dialog is
        // asked to open on the very first frame. No dialog, and no way out of
        // the room.
        setControllerReady(true);
      });
    }
    return () => setControllerReady(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    controllerRef.current?.updateDimensions(dimensionsContext, uiSymbolSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // The win and the bet it was made at, pushed in before the sequence starts —
  // the same order PotCollectionMaxWinDialog uses, so the value is on screen
  // from the first frame rather than popping in.
  useEffect(() => {
    if (controllerReady && controllerRef.current && show) {
      fsDiag("totalWinDialog -> startSequence", { totalWin, bet: effectiveBet });
      controllerRef.current.setTotalWinValue(totalWin, effectiveBet);
      controllerRef.current.startSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, controllerReady]);

  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt?.preventDefault) evt.preventDefault();
      if (evt?.stopPropagation) evt.stopPropagation();
      if (!controllerRef.current || !show) return;
      // No sound here — see the controller's playClosingAnimation. This
      // returns true for BOTH a counter skip and a close, so playing on it
      // sounded the first tap as well as the second.
      controllerRef.current.handleUserClick();
    },
    [show],
  );

  // Spacebar dismisses too, matching the intro.
  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // No sound here either — see handleBackgroundClick above. The spacebar
      // is the path Vladimir reported it on, 2026-09-18: one sound on the
      // press that stops the counter, another on the press that closes.
      controllerRef.current?.handleUserClick();
    };

    document.addEventListener("keydown", handleKeyDown, {
      capture: true,
      passive: false,
    });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [show]);

  useEffect(() => {
    return () => {
      controllerRef.current?.cleanup();
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  const containerPosition = useMemo(
    () => ({ x: dimensions.width / 2, y: dimensions.height / 2 }),
    [dimensions.width, dimensions.height],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name={isHoldAndWin ? "hold_and_win_total_win" : "free_spin_total_win"}
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={containerRef}
      // Gates the WHOLE subtree. The tap-to-continue container is eventMode
      // "static" with its own hit area, and closing only drops the spine's
      // alpha — so without this it stays invisible but still swallowing
      // pointer events, for every popup, for ever. Every Zeus popup carries
      // this guard; these five were missing it.
      eventMode={show ? "auto" : "none"}
    >
      {/* Drawn back to 0,0 so the rect covers the screen while the spine
          stays centred. */}
      <Graphics
        name="free_spin_total_win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={show ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        // Clickable, but NEVER a pointer — only the tap word offers one.
        cursor="default"
      />
      {/* Spine, label and tap-to-continue are owned by the controller and
          pinned into the spine's own slots. */}
    </Container>
  );
};

export default memo(FreeSpinTotalWinDialog);
